import React from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input/Input';
import { apiFetch } from '@/lib/api';
import { useNotificationStore } from '@/stores/notificationStore';
import { t } from '@/lib/i18n';

interface InviteResult {
  id: string;
  email: string;
  expiresAt: string;
  emailSent: boolean;
  inviteLink?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function InviteSuperAdminDialog({ isOpen, onClose, onSuccess }: Props) {
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState<InviteResult | null>(null);
  const notifications = useNotificationStore();

  const handleClose = () => {
    if (submitting) return;
    setEmail('');
    setResult(null);
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    setSubmitting(true);
    try {
      const res = await apiFetch('/api/admin/super-admin-invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const json = await res.json();
      if (!json.success) {
        throw new Error(json.error?.message || t('Failed to send invitation'));
      }
      setResult(json.data);
      notifications.success(t('Super admin invitation sent'));
      onSuccess();
    } catch (e: any) {
      notifications.error(e.message || t('Failed to send invitation'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={result ? t('Invitation Created') : t('Invite Super Admin')}
      size="md"
    >
      {result ? (
        <div className="space-y-4">
          {result.emailSent ? (
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-success-700 font-medium mb-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('Invitation email sent')}
              </div>
              <p className="text-sm text-success-600">
                {t('The invitation email has been sent. They have 7 days to accept.')}
              </p>
            </div>
          ) : (
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-warning-700 font-medium mb-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                {t('Email not configured')}
              </div>
              <p className="text-sm text-warning-600">
                {t('No email provider is set up. Share the invite link manually.')}
              </p>
            </div>
          )}

          {result.inviteLink && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('Invite Link')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={result.inviteLink}
                  className="flex-1 px-3 py-2 text-sm bg-muted border border-border rounded-lg text-muted-foreground font-mono truncate"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(result.inviteLink!);
                    notifications.success(t('Link copied to clipboard'));
                  }}
                >
                  {t('Copy')}
                </Button>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t('Share this link with the invitee. It expires in 7 days.')}
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={handleClose}>{t('Done')}</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-muted rounded-lg p-4 text-sm text-muted-foreground">
            {t('Super admins have full platform-wide access across every organization and all admin settings. Invite sparingly.')}
          </div>

          <div>
            <Input
              label={t('Email Address')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('Enter email address')}
              required
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && email.trim() && !submitting) {
                  handleSubmit();
                }
              }}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={!email.trim() || submitting}
              isLoading={submitting}
              className="flex-1"
            >
              {t('Send Invitation')}
            </Button>
            <Button variant="outline" onClick={handleClose} disabled={submitting}>
              {t('Cancel')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
